import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  SwitchAccount as SwitchAccountIcon,
} from '@mui/icons-material';
import { userService, type User } from '../../services/user.service';
import api from '../../services/api';
import authService from '../../services/auth.service';
import { rolePermissionService, type Role } from '../../services/rolePermissionService';

interface FirmOption {
  id: number;
  name: string;
}

const emptyForm = {
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  firm_id: '' as number | '',
  roleNames: [] as string[],
  is_approved: true,
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'view' | 'edit' | 'delete'>('view');
  const [firms, setFirms] = useState<FirmOption[]>([]);
  const [rolesList, setRolesList] = useState<Role[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [masqueradingUserId, setMasqueradingUserId] = useState<number | null>(null);

  const currentUser = authService.getCurrentUser();
  const canMasquerade = authService.hasRole('admin') && !authService.isMasquerading();

  const canMasqueradeAsUser = (user: User): boolean => {
    if (!canMasquerade || user.id === currentUser?.id) {
      return false;
    }

    if (user.roles.some((role) => role.name === 'admin')) {
      return false;
    }

    if (!user.is_approved) {
      return false;
    }

    const isFirmAdmin = user.roles.some((role) => role.name === 'firm_admin');
    if (isFirmAdmin && user.firm?.status && user.firm.status !== 'approved') {
      return false;
    }

    return true;
  };

  const handleMasquerade = async (user: User) => {
    if (!window.confirm(`Log in as ${user.name}? You can switch back at any time.`)) {
      return;
    }

    setMasqueradingUserId(user.id);
    try {
      await authService.startMasquerade(user.id);
      toast.success(`Now viewing as ${user.name}`);
      window.location.href = '/dashboard';
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to start masquerade session';
      toast.error(msg);
    } finally {
      setMasqueradingUserId(null);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadFirmsAndRoles = async () => {
    try {
      const [firmsRes, rolesRes] = await Promise.all([
        api.get<{ data: FirmOption[] }>('/admin/firms'),
        rolePermissionService.getRoles(),
      ]);
      setFirms(Array.isArray(firmsRes.data?.data) ? firmsRes.data.data : []);
      setRolesList(rolesRes.data?.roles || []);
    } catch (e) {
      console.error('Error loading firms/roles:', e);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      setUsers(response.data?.data ?? []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setForm(emptyForm);
    setSelectedUser(null);
    setDialogType('edit');
    setDialogOpen(true);
    loadFirmsAndRoles();
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      password_confirmation: '',
      firm_id: user.firm?.id ?? '',
      roleNames: user.roles?.map((r) => r.name) ?? [],
      is_approved: user.is_approved ?? true,
    });
    setDialogType('edit');
    setDialogOpen(true);
    loadFirmsAndRoles();
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setDialogType('view');
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => openEditDialog(user);

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDialogType('delete');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    const isAdd = !selectedUser;
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (isAdd && !form.password) {
      toast.error('Password is required for new users');
      return;
    }
    if (isAdd && form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (isAdd && form.password !== form.password_confirmation) {
      toast.error('Password confirmation does not match');
      return;
    }
    setSubmitting(true);
    try {
      if (isAdd) {
        await userService.createUser({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          password_confirmation: form.password_confirmation,
          firm_id: form.firm_id || null,
          roles: form.roleNames,
          is_approved: form.is_approved,
        });
        toast.success('User created successfully');
      } else {
        const payload: { name: string; email: string; password?: string; password_confirmation?: string; firm_id: number | null; roles: string[]; is_approved: boolean } = {
          name: form.name.trim(),
          email: form.email.trim(),
          firm_id: form.firm_id || null,
          roles: form.roleNames,
          is_approved: form.is_approved,
        };
        if (form.password) {
          payload.password = form.password;
          payload.password_confirmation = form.password_confirmation;
        }
        await userService.updateUser(selectedUser!.id, payload);
        toast.success('User updated successfully');
      }
      handleCloseDialog();
      loadUsers();
    } catch (err: any) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err.response?.data?.message || 'Failed to save user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleColor = (roleName: string) => {
    const colors: { [key: string]: string } = {
      'admin': 'error',
      'firm_admin': 'primary',
      'expert_user': 'warning',
      'user': 'default'
    };
    return colors[roleName] || 'default';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
        >
          Add User
        </Button>
      </Box>

      <Card>
        <CardHeader
          title="Users"
          action={
            <IconButton onClick={loadUsers}>
              <RefreshIcon />
            </IconButton>
          }
        />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Firm</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.firm?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {user.roles.map((role) => (
                        <Chip
                          key={role.id}
                          label={role.name.replace('_', ' ')}
                          color={getRoleColor(role.name) as any}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_approved ? 'Approved' : 'Pending'}
                        color={user.is_approved ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewUser(user)}
                        title="View"
                      >
                        <ViewIcon />
                      </IconButton>
                      {canMasqueradeAsUser(user) && (
                        <IconButton
                          size="small"
                          onClick={() => handleMasquerade(user)}
                          title="Log in as this user"
                          color="secondary"
                          disabled={masqueradingUserId === user.id}
                        >
                          <SwitchAccountIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'view' && 'View User'}
          {dialogType === 'edit' && (selectedUser ? 'Edit User' : 'Add User')}
          {dialogType === 'delete' && 'Delete User'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'view' && selectedUser && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedUser.name}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                {selectedUser.email}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Firm:</strong> {selectedUser.firm?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Roles:</strong> {selectedUser.roles.map(r => r.name.replace('_', ' ')).join(', ')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> {selectedUser.is_approved ? 'Approved' : 'Pending'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Registration:</strong> {selectedUser.registration_completed ? 'Completed' : 'Incomplete'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Created:</strong> {formatDate(selectedUser.created_at)}
              </Typography>
            </Box>
          )}

          {dialogType === 'edit' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={selectedUser ? 'New password (leave blank to keep)' : 'Password'}
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                margin="normal"
                required={!selectedUser}
              />
              <TextField
                fullWidth
                label="Confirm password"
                type="password"
                value={form.password_confirmation}
                onChange={(e) => setForm((f) => ({ ...f, password_confirmation: e.target.value }))}
                margin="normal"
                required={!selectedUser}
              />
              <InputLabel id="firm-select-label">Firm</InputLabel>
              <Select
                labelId="firm-select-label"
                value={form.firm_id === '' ? '' : form.firm_id}
                onChange={(e) => {
                  const v = String(e.target.value);
                  setForm((f) => ({ ...f, firm_id: v === '' ? '' : Number(v) }));
                }}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {firms.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
              <InputLabel id="roles-select-label">Roles</InputLabel>
              <Select
                labelId="roles-select-label"
                multiple
                value={form.roleNames}
                onChange={(e) => setForm((f) => ({ ...f, roleNames: e.target.value as string[] }))}
                renderValue={(selected) => (selected as string[]).map((n) => n.replace('_', ' ')).join(', ')}
                fullWidth
              >
                {rolesList.map((r) => (
                  <MenuItem key={r.id} value={r.name}>
                    {r.name.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_approved}
                    onChange={(e) => setForm((f) => ({ ...f, is_approved: e.target.checked }))}
                  />
                }
                label="Approved"
              />
            </Box>
          )}

          {dialogType === 'delete' && selectedUser && (
            <Alert severity="warning">
              Are you sure you want to delete user &quot;{selectedUser.name}&quot;? This action cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            {dialogType === 'delete' ? 'Cancel' : 'Close'}
          </Button>
          {dialogType === 'edit' && (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving…' : selectedUser ? 'Update' : 'Create'}
            </Button>
          )}
          {dialogType === 'delete' && (
            <Button variant="contained" color="error" onClick={handleCloseDialog}>
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers; 